import { api } from '../api';

export interface BajaItem {
  asociadoId: string;
  documento: string;
  nombre: string;
  legajo: string;
  esTitular: boolean;
  motivo: string;
  fecha: string;
  detalle: string;
  bajaAt: string;
  bajaPor: string;
}
export interface BajasPage {
  items: BajaItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CandidatoBaja {
  asociadoId: string;
  documento: string;
  nombre: string;
  legajo: string;
  esTitular: boolean;
}

export interface PreviewBajaRow {
  dni: string;
  encontrado: boolean;
  detalle?: string;
  asociadoId?: string;
  nombre?: string;
  legajo?: string;
  legajoArchivo?: string;
  esTitular?: boolean;
  yaBaja?: boolean;
  motivo?: string;
  fecha?: string;
}
export interface PreviewBaja {
  total: number;
  encontrados: number;
  noEncontrados: number;
  ambiguos: number;
  yaBaja: number;
  columnas: { dni: boolean; legajo: boolean; motivo: boolean; fecha: boolean };
  items: PreviewBajaRow[];
}

export interface MarcarBajaBody {
  asociadoIds: string[];
  motivo: string;
  fecha?: string;
  detalle?: string;
  tambienLegajo?: boolean;
}

/** Candidatos a marcar de baja (personas no dadas de baja). */
export async function buscarCandidatosBaja(q: string): Promise<{ items: CandidatoBaja[] }> {
  const { data } = await api.get<{ items: CandidatoBaja[] }>('/bajas/buscar', { params: { q } });
  return data;
}

/** Previsualiza un archivo (clasifica + cruza contra Mongo, sin escribir). */
export async function previewArchivoBajas(file: File): Promise<PreviewBaja> {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post<PreviewBaja>('/bajas/preview', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/** Marca personas como baja (ESCRITURA — deshabilitada en modo solo estructura). */
export async function marcarBajas(
  body: MarcarBajaBody,
): Promise<{ marcados: number; legajosBaja: number }> {
  const { data } = await api.post<{ marcados: number; legajosBaja: number }>('/bajas/marcar', body);
  return data;
}

/** Revierte la baja de personas (ESCRITURA — deshabilitada en modo solo estructura). */
export async function desmarcarBajas(asociadoIds: string[]): Promise<{ desmarcados: number }> {
  const { data } = await api.post<{ desmarcados: number }>('/bajas/desmarcar', { asociadoIds });
  return data;
}

// ===========================================================================
// Baja de INTEGRANTES en Binary (feature bajas-automaticas)
// ===========================================================================

export type ClasifBaja =
  | 'listo'
  | 'titular_solo'
  | 'grupo_completo'
  | 'sin_motivo'
  | 'motivo_invalido'
  | 'sin_fecha_fallecimiento'
  | 'vigencia_antes_fallecimiento'
  | 'duplicado_archivo'
  | 'plan_desconocido'
  | 'fallecimiento_con_grupo'
  | 'fallecimiento_manual'
  | 'titular'
  | 'ya_baja'
  | 'no_activo'
  | 'no_encontrado'
  | 'ambiguo';

/** Clasificaciones que van a la sección "Alertas Bajas" (no se procesan). */
export const CLASIF_ALERTA: ClasifBaja[] = [
  'plan_desconocido',
  'titular',
  'fallecimiento_con_grupo',
  'fallecimiento_manual',
];
/** Clasificaciones procesables (se dan de baja de verdad). */
export const CLASIF_PROCESABLE: ClasifBaja[] = ['listo', 'titular_solo', 'grupo_completo'];

/** Mapa clasif de alerta → tipo de alerta del backend. */
export const CLASIF_A_TIPO_ALERTA: Partial<Record<ClasifBaja, AlertaBajaTipo>> = {
  plan_desconocido: 'plan_desconocido',
  titular: 'titular_con_grupo',
  fallecimiento_con_grupo: 'fallecimiento_con_grupo',
  fallecimiento_manual: 'fallecimiento_manual',
  // Errores de dato: si se corrigen en la revisión la fila se da de baja; si
  // se confirma el lote sin corregirlos, quedan registrados como alerta.
  motivo_invalido: 'motivo_invalido',
  sin_motivo: 'sin_motivo',
  sin_fecha_fallecimiento: 'sin_fecha_fallecimiento',
  vigencia_antes_fallecimiento: 'vigencia_antes_fallecimiento',
};

export interface PreviewBajaBinItem {
  dni: string;
  asociadoId?: string;
  nombre?: string;
  legajo?: string;
  cuil?: string;
  afiId?: string;
  resueltoPor?: 'cuil' | 'afiId' | 'webId';
  tbCodigo?: string;
  fechaVigencia?: string;
  /** Fecha real del deceso (dd/mm/aaaa). Solo aplica con tbCodigo 37020. */
  fechaFallecimiento?: string;
  /** Plan del grupo en Binary (esta etapa da de baja solo MS1). */
  plan?: 'MS1' | 'MS2' | 'otro' | null;
  esTitular: boolean;
  clasif: ClasifBaja;
  detalle?: string;
  /**
   * La baja no se puede hacer sola: hay que repartir antes al grupo que queda.
   *
   * Es una propiedad de la fila y no algo que se deduzca del `clasif` porque son
   * dos caminos distintos que llegan al mismo lugar (fallecimiento del titular
   * con grupo, y baja por empresa de un titular cuya familia no viene completa
   * en el archivo), y en el medio puede aparecer un tercero.
   */
  requiereRearmado?: boolean;
}
export interface PreviewBajaBin {
  total: number;
  listos: number;
  sinMotivo: number;
  titulares: number;
  yaBaja: number;
  errores: number;
  /** Filas que van a "Alertas Bajas" (fallecimiento con grupo, retroactivo, plan sin determinar). */
  alertas: number;
  columnas: {
    dni: boolean;
    legajo: boolean;
    motivo: boolean;
    vigencia: boolean;
    fechaFallecimiento: boolean;
  };
  /** Motivos habilitados, servidos por el backend (antes estaban hardcodeados). */
  motivos?: Array<{ codigo: string; label: string }>;
  items: PreviewBajaBinItem[];
}

export interface BajaBinResultado {
  asociadoId: string;
  documento: string;
  nombre: string;
  legajo: string;
  afiId?: string;
  tbCodigo?: string;
  fechaVigencia?: string;
  estado: 'baja_ok' | 'rechazada' | 'omitida' | 'error';
  solafiId?: string;
  vdaDrv?: string;
  error?: string;
  /** `rearmado` = se repartió el grupo antes de dar de baja al titular. */
  tipo?: 'baja' | 'rearmado';
  /** Cómo quedó armado el grupo. Solo en los rearmados. */
  detalle?: string;
  rearmadoId?: string;
}
export interface BajaJobResumen {
  jobId: string;
  estado: 'corriendo' | 'terminado' | 'error';
  total: number;
  procesados: number;
  exitosos: number;
  fallidos: number;
  nombreArchivo?: string;
  startedAt?: string;
  finishedAt?: string;
  error?: string;
  resultados?: BajaBinResultado[];
}

export interface ConfirmarBajaBinBody {
  items: Array<{
    asociadoId: string;
    tbCodigo: string;
    fechaVigencia: string;
    fechaFallecimiento?: string;
  }>;
  bloqueados?: Array<{ documento?: string; nombre?: string; legajo?: string; motivo: string }>;
  /** Filas que van a "Alertas Bajas" (clasificadas como alerta en el preview). */
  alertas?: Array<{
    asociadoId?: string;
    documento?: string;
    nombre?: string;
    legajo?: string;
    cuil?: string;
    afiId?: string;
    tipo: AlertaBajaTipo;
    mensaje: string;
    tbCodigo?: string;
    fechaVigencia?: string;
    fechaFallecimiento?: string;
  }>;
  /**
   * Los rearmados de grupo planificados en la revisión.
   *
   * NO se ejecutan al armarlos: viajan con el lote y corren dentro del job, como
   * el resto de las filas.
   */
  rearmados?: RearmarGrupoBody[];
  nombreArchivo?: string;
}

// ---- Alertas Bajas ----

export type AlertaBajaTipo =
  | 'ms2'
  | 'fallecimiento_con_grupo'
  | 'fallecimiento_manual'
  | 'rechazo_binary'
  // Datos incompletos del archivo que el operador no corrigió en la revisión.
  | 'motivo_invalido'
  | 'sin_fecha_fallecimiento'
  | 'vigencia_antes_fallecimiento'
  | 'sin_motivo'
  // El plan del grupo no se pudo leer, o no es ninguno de los que esta etapa
  // procesa. La empresa pidió la baja y no se hizo: tiene que verlo alguien.
  | 'plan_desconocido'
  // Titular con familia activa parcial y sin rearmado. Mismo criterio: la baja
  // se pidió y no se hizo, así que queda pendiente de gestionarse a mano.
  | 'titular_con_grupo';
export type AlertaBajaEstado = 'abierta' | 'resuelta' | 'archivada';

export interface AlertaBaja {
  id: string;
  /** Qué se encontró en Binary al marcarla como gestionada. */
  verificacion?: VerificacionAlerta;
  asociadoId: string;
  documento: string;
  cuil: string;
  nombre: string;
  legajo: string;
  afiId: string;
  tipo: AlertaBajaTipo;
  mensaje: string;
  tbCodigo: string;
  fechaVigencia: string;
  fechaFallecimiento: string;
  solafiId: string;
  jobId: string;
  estado: AlertaBajaEstado;
  nombreArchivo: string;
  creadoPor: string;
  creadoAt: string | null;
  resueltoPor: string;
  resueltoAt: string | null;
}
export interface AlertasBajasPage {
  items: AlertaBaja[];
  total: number;
  page: number;
  pageSize: number;
}

export interface UltimaBaja {
  asociadoId: string;
  documento: string;
  cuil: string;
  nombre: string;
  legajo: string;
  afiId: string;
  motivo: string;
  tbCodigo: string;
  fechaVigencia: string;
  solafiId: string;
  ejecutadoPor: string;
  at: string;
}
export interface UltimasBajasPage {
  items: UltimaBaja[];
  total: number;
  page: number;
  pageSize: number;
}

/** Previsualiza el archivo: clasifica y resuelve el afi_id de cada integrante (read-only). */
export async function previewBajaBinary(file: File): Promise<PreviewBajaBin> {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post<PreviewBajaBin>('/bajas-binary/preview', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/** Descarga la plantilla del archivo de bajas (con anexos de motivos y reglas). */
export async function descargarPlantillaBajas(): Promise<void> {
  const res = await api.get('/bajas-binary/plantilla', { responseType: 'blob' });
  const cd = (res.headers['content-disposition'] as string | undefined) ?? '';
  const filename = /filename="?([^"]+)"?/i.exec(cd)?.[1] ?? 'plantilla_bajas.xlsx';
  const blob = new Blob([res.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** Confirma la baja en Binary de los integrantes revisados (arranca un job async). */
export async function confirmarBajaBinary(body: ConfirmarBajaBinBody): Promise<BajaJobResumen> {
  const { data } = await api.post<BajaJobResumen>('/bajas-binary/confirmar', body);
  return data;
}

/** Estado/progreso de un job de baja. */
export async function getBajaJob(jobId: string): Promise<BajaJobResumen> {
  const { data } = await api.get<BajaJobResumen>(`/bajas-binary/jobs/${jobId}`);
  return data;
}

/**
 * De dónde salió la baja. `portal` = la despachó el micrositio contra Binary;
 * `todas` incluye además las que se hicieron por fuera (operador, ABM) y acá
 * solo se espejaron al reconciliar.
 */
export type OrigenBaja = 'todas' | 'portal';

/** Últimas bajas hechas, con búsqueda por DNI/CUIL/nombre/legajo/N° solicitud. */
export async function getUltimasBajas(
  page = 1,
  pageSize = 10,
  q = '',
  origen: OrigenBaja = 'todas',
): Promise<UltimasBajasPage> {
  const { data } = await api.get<UltimasBajasPage>('/bajas-binary/ultimas', {
    // `todas` no viaja: el server trata cualquier valor que no sea `portal`
    // como "sin filtro", y así el default queda en un solo lugar.
    params: { page, pageSize, q, ...(origen === 'portal' ? { origen } : {}) },
  });
  return data;
}

/** Alertas de bajas (casos manuales + rechazos de Binary). Filtros q/estado/tipo. */
export async function getAlertasBajas(opts: {
  page?: number;
  pageSize?: number;
  q?: string;
  estado?: string;
  tipo?: string;
}): Promise<AlertasBajasPage> {
  const { data } = await api.get<AlertasBajasPage>('/bajas-binary/alertas', {
    params: {
      page: opts.page ?? 1,
      pageSize: opts.pageSize ?? 20,
      q: opts.q || undefined,
      estado: opts.estado || undefined,
      tipo: opts.tipo || undefined,
    },
  });
  return data;
}

/** Cambia el estado de una alerta (resuelta | archivada | abierta).
 *  POST (no PATCH): el gateway Kong solo rutea GET/POST para este servicio. */
/** Verificación contra Binary que hace el server al marcar una alerta gestionada. */
export interface VerificacionAlerta {
  estadoBinary: string | null;
  activo: boolean;
  esCabeza: boolean;
  espejado: boolean;
  legajoMarcado: boolean;
  mensaje: string;
  verificadoAt: string;
}

export async function resolverAlertaBaja(
  id: string,
  estado: AlertaBajaEstado,
): Promise<{ id: string; estado: string; verificacion?: VerificacionAlerta }> {
  const { data } = await api.post<{
    id: string;
    estado: string;
    verificacion?: VerificacionAlerta;
  }>(`/bajas-binary/alertas/${id}/estado`, { estado });
  return data;
}

// ---- Rearmado de grupo (baja del titular dejando a otros a cargo) ----

/** Qué hay que cargarle a alguien antes de poder dejarlo a cargo de un grupo. */
export type RequisitoNuevoTitular = 'tutor' | 'curador' | 'domicilio';

/** Por qué alguien no puede quedar a cargo del grupo. */
export type MotivoNoElegible =
  | 'es_el_que_se_da_de_baja'
  | 'no_activo'
  | 'parentesco_no_habilitado'
  | 'hijo_mayor_sin_cud'
  | 'sin_fecha_de_nacimiento';

export interface ContinuidadIntegrante {
  afiId: string;
  /** Su `_id` en el padrón, si se lo pudo cruzar. Binary tiene gente que el padrón no conoce. */
  asociadoId?: string;
  parentesco: string | null;
  parentescoLabel: string;
  fechaNacimiento: string | null;
  edad: number | null;
  discapacitado: boolean;
  cuil: string | null;
  tieneDomicilio: boolean;
  elegible: boolean;
  motivo?: MotivoNoElegible;
  requiere?: RequisitoNuevoTitular;
  /** TODO lo que falta cargar, no solo lo primero: son trámites distintos. */
  requisitos?: RequisitoNuevoTitular[];
  /**
   * Con cuáles de los candidatos puede quedar: los afi_id para los que la
   * relación se puede deducir sin inventarla.
   *
   * Lo calcula el server con la misma función que después valida el reparto, así
   * que la pantalla no repite la regla. Sin esto dejaba elegir "con el hijo de
   * 25" para un adherente y el rechazo llegaba recién al confirmar.
   */
  puedeSeguirA: string[];
  detalle: string;
}

export interface ContinuidadResult {
  titular: {
    afiId: string;
    documento: string;
    nombre: string;
    legajo: string;
    plan: 'MS1' | 'MS2' | 'otro' | null;
    planCodigo: string | null;
    cuil: string | null;
  };
  integrantes: ContinuidadIntegrante[];
  hayContinuidadPosible: boolean;
  /** Nadie puede continuar: la baja del titular se lleva el grupo completo. */
  caeElGrupo: boolean;
}

/** Una rama del reparto: quién queda a cargo y quiénes van con él. */
export interface GrupoRearmado {
  titularAfiId: string;
  integrantesAfiIds: string[];
}

export interface RearmarGrupoBody {
  documento: string;
  grupos: GrupoRearmado[];
  /**
   * Los que no siguen en ningún grupo: se dan de baja con el titular, con el
   * mismo motivo y la misma vigencia.
   *
   * Es la salida para el adherente o el nieto que nadie se lleva, y para el que
   * no tiene un parentesco deducible con ningún candidato.
   */
  dadosDeBaja?: string[];
  motivo: string;
  fechaVigencia: string;
  fechaFallecimiento?: string;
}

export type PasoRearmado = 'modif_tit_paren' | 'baja_saliente' | 'espejo_mongo';
export type EstadoRearmado = 'corriendo' | 'ok' | 'fallado';

export interface PasoRearmadoEjecutado {
  paso: PasoRearmado;
  ok: boolean;
  solafiId?: number;
  vdaDrv?: string;
  error?: string;
  at: string;
}

export interface RearmadoResultado {
  id: string;
  estado: EstadoRearmado;
  pasos: PasoRearmadoEjecutado[];
}

/** Qué se le puede mostrar al usuario de cada paso cuando algo falla. */
export const PASO_REARMADO_LABEL: Record<PasoRearmado, string> = {
  modif_tit_paren: 'Armar el grupo nuevo',
  baja_saliente: 'Dar de baja al titular',
  espejo_mongo: 'Actualizar el legajo en el portal',
};

/** Quién puede quedar a cargo del grupo si se da de baja a este titular (read-only). */
export async function getContinuidad(documento: string): Promise<ContinuidadResult> {
  const { data } = await api.get<ContinuidadResult>('/bajas-binary/continuidad', {
    params: { documento },
  });
  return data;
}

/**
 * Ejecuta UN rearmado suelto, fuera del lote.
 *
 * La pantalla de Bajas NO lo usa: ahí el rearmado se planifica y viaja en
 * `confirmarBajaBinary`, para que el operador vea la configuración de cada grupo
 * nuevo antes de que se escriba nada. Queda expuesto para uso por consola
 * (`pruebas-bajas-v2/rearmar.sh`) y para el día que haya un rearmado por legajo
 * sin archivo de por medio.
 */
export async function rearmarGrupo(body: RearmarGrupoBody): Promise<RearmadoResultado> {
  const { data } = await api.post<RearmadoResultado>('/bajas-binary/rearmar', body);
  return data;
}
